import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];
    const pages = getDataView(state.data.pages, { parentNodeId: nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: nodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[nodeId] ?? {
            parentNodeId: nodeId,
        },
        viewAll: getPageLinkByPageType(state, "NewsListPage")?.url,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    viewAllLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    title?: InjectableCss;
    header?: TextFieldPresentationProps;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
        `,
    },
    newsWrapper: {
        css: css`
            margin-bottom: 12px;
            max-width: 33.3%;
            width: 33.3%;
            padding: 0 5px;
            .news-wrapper {
                display: flex;
            }
            .news-content {
                width: 70%;
                padding-left: 10px;
                margin-bottom: 12px;
                h3 {
                    font-size: 14px;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    a {
                        & :focus {
                            outline: none;
                        }
                        span {
                            color: ${COLORS.black};
                        }
                    }
                }

                h5 {
                    font-size: ${TYPOGRAPHY.size.tiny};
                    color: #0b70b7;
                    // margin-bottom: 0.5em !important;
                    margin-bottom: 5px;
                }

                p {
                    display: none;
                }
            }

            .newz-img {
                width: 5.625rem;
                height: 60px;
                .img-wrapper {
                    img {
                        height: inherit;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 100%;
                            width: 100%;
                            padding: 0;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            padding: 25px 25px 0 20px;
            border-bottom: 1px solid #0000000f;
            border-radius: 10px;
            flex-wrap: wrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            border-radius: 10px;
            background: #f1f6fb;
        `,
    },
    title: {
        css: css`
            // background: transparent;
            width: 100%;
            color: ${COLORS.black};
            font-size: ${TYPOGRAPHY.size.small};
            padding: 14px 25px;
            font-weight: ${TYPOGRAPHY.weight.bold};
            border-radius: 10px 10px 0 0;
            border-bottom: 1.5px solid #0b70b7;
            display: none;
        `,
    },
    viewAllLink: {
        css: css`
            display: flex;
            justify-content: center;
            font-size: ${TYPOGRAPHY.size.smaller};
            padding: 10px;
            font-weight: bold;
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && this.props.parentNodeId && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && this.props.parentNodeId && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
            viewAll,
        } = this.props;

        if (!pages) {
            return null;
        }

        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"])
            .reverse()
            .slice((page - 1) * pageSize, page * pageSize);

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.title} className="NewEvent-title">
                    {translate("News & Event")}
                </StyledWrapper>
                <StyledWrapper {...styles.listWrapper} className="detailList">
                    {sortedAndPagedPages.map((page: PageProps) => (
                        <StyledWrapper {...styles.newsWrapper} key={page.id} className="newsWrapper">
                            {/* <Typography variant="h2" {...styles.titleText}>
                                <Link href={links[page.nodeId]?.url}>{page.fields["title"]}</Link>
                            </Typography> */}
                            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                {page.fields["author"] && (
                                    <Typography {...styles.authorText}>{page.fields["author"]}</Typography>
                                )}
                                {/* {page.fields["publishDate"] && (
                                    <Typography {...styles.publishDateText}>
                                        <LocalizedDateTime
                                            dateTime={new Date(page.fields["publishDate"])}
                                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                        />
                                    </Typography>
                                )} */}
                            </StyledWrapper>
                            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                <StyledWrapper {...styles.summaryWrapper}>
                                    {parse(
                                        page.fields["newsSummary"] ||
                                            truncate(page.fields["newsContent"], { length: 500 }),
                                        parserOptions,
                                    )}
                                </StyledWrapper>
                            )}
                            {/* <Link href={links[page.nodeId]?.url} {...styles.readMoreLink}>
                            {translate("Read More")}
                        </Link> */}
                        </StyledWrapper>
                    ))}
                    {/* {totalItems > pageSize && (
                    <Pagination
                        resultsCount={totalItems}
                        currentPage={page}
                        resultsPerPage={pageSize}
                        resultsPerPageOptions={[
                            defaultPageSize,
                            defaultPageSize * 2,
                            defaultPageSize * 3,
                            defaultPageSize * 4,
                        ]}
                        onChangePage={this.changePage}
                        onChangeResultsPerPage={this.changeResultsPerPage}
                        pageSizeCookie="NewsList-PageSize"
                        {...styles.pagination}
                    />
                )} */}
                </StyledWrapper>
                <Link href={viewAll} {...styles.viewAllLink}>
                    {translate("View all")}
                </Link>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(NewsListView)),
    definition: {
        group: "News List",
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "Translatable",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
